if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        // Keep slider-control and thumbnail-strip interactions from bubbling
        // into the ModalDialog close-on-pointerup listeners.
        this.addEventListener(
          'pointerup',
          (event) => {
            if (event.target.closest('.slider-buttons, .product-media-modal__thumbs')) event.stopPropagation();
          },
          true
        );

        // Touch: a tap on the empty area around the image closes the modal.
        // Swipes end in pointercancel or move beyond the threshold, so they
        // never dismiss; taps on media or controls are excluded.
        this.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' || !event.isPrimary) return;
          this.touchStart = { x: event.clientX, y: event.clientY };
        });
        this.addEventListener('pointerup', (event) => {
          if (event.pointerType === 'mouse' || !event.isPrimary || !this.touchStart) return;
          const moved = Math.hypot(event.clientX - this.touchStart.x, event.clientY - this.touchStart.y);
          this.touchStart = null;
          if (moved > 12) return;
          if (
            event.target.closest(
              'img, button, a, deferred-media, product-model, .slider-buttons, .product-media-modal__thumbs'
            )
          )
            return;
          this.hide();
        });

        // A pointerup can only reach a closed modal when that very event just
        // closed it. Its trailing click would hit-test against the revealed
        // page (often the gallery's modal-opener, reopening the lightbox) —
        // swallow it.
        this.addEventListener('pointerup', () => {
          if (this.hasAttribute('open')) return;
          const swallowTrailingClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
          };
          window.addEventListener('click', swallowTrailingClick, { capture: true, once: true });
          setTimeout(() => window.removeEventListener('click', swallowTrailingClick, { capture: true }), 400);
        });

        // Thumbnail strip: tap a thumb to jump the carousel to that media.
        this.addEventListener('click', (event) => {
          const thumb = event.target.closest('.product-media-modal__thumbs [data-target]');
          if (!thumb) return;
          this.setActiveThumb(thumb.dataset.target);
          const media = this.querySelector(`.product-media-modal__slider [data-media-id="${thumb.dataset.target}"]`);
          const slide = media?.closest('.product-media-modal__slide');
          const slider = this.querySelector('[id^="Slider-Lightbox-"]');
          if (slide && slider) slider.scrollTo({ left: slide.offsetLeft });
        });

        // Carousel swipe -> highlight the matching thumb. slideChanged does
        // not bubble, so listen in the capture phase; the listener lives on
        // <product-modal> and survives variant-change innerHTML swaps.
        this.addEventListener(
          'slideChanged',
          debounce((event) => {
            const mediaId = event.detail?.currentElement
              ?.querySelector('[data-media-id]')
              ?.getAttribute('data-media-id');
            if (mediaId) this.setActiveThumb(mediaId);
          }, 100),
          true
        );
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        if (!this.imagesLoaded) {
          this.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
          this.imagesLoaded = true;
        }
        this.showActiveMedia();
      }

      setActiveThumb(mediaId) {
        const thumbs = this.querySelector('.product-media-modal__thumbs');
        if (!thumbs || !mediaId) return;
        const item = thumbs.querySelector(`[data-target="${mediaId}"]`);
        if (!item) return;
        thumbs.querySelectorAll('button').forEach((button) => button.removeAttribute('aria-current'));
        item.querySelector('button')?.setAttribute('aria-current', 'true');
        if (typeof thumbs.isSlideVisible === 'function' && thumbs.slider && !thumbs.isSlideVisible(item, 10)) {
          thumbs.slider.scrollTo({ left: item.offsetLeft });
        }
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        if (!activeMedia) return;
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        if (window.matchMedia('(min-width: 750px)').matches) {
          activeMedia.scrollIntoView();
        } else {
          const slider = this.querySelector('[id^="Slider-Lightbox-"]');
          const slide = activeMedia.closest('.product-media-modal__slide');
          if (slider && slide) {
            slider.style.scrollBehavior = 'auto';
            slider.scrollLeft = slide.offsetLeft;
            slider.style.scrollBehavior = '';
            const sliderComponent = slider.closest('slider-component');
            if (typeof sliderComponent?.update === 'function') sliderComponent.update();
          }
          this.setActiveThumb(this.openedBy.getAttribute('data-media-id'));
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
